import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/types/news';
import { formatNumber, timeAgo } from '@/utils/news';

export default function HeroCarousel({ articles }: { articles: Article[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (articles.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % articles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [articles.length]);

  if (articles.length === 0) {
    return (
      <div className="flex h-72 items-center justify-center rounded-lg border border-background-200 bg-background-50 text-foreground-400">
        暂无当前热点
      </div>
    );
  }

  const current = articles[active];

  return (
    <div className="relative h-full min-h-[300px] w-full overflow-hidden rounded-lg md:min-h-[420px] lg:min-h-[460px]">
      {articles.map((item, index) => (
        <Link
          key={item.id}
          to={`/detail/${item.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 热搜榜`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent"></div>
        </Link>
      ))}

      <div className="pointer-events-none absolute left-0 top-0 flex w-full items-center justify-between p-4 md:p-6">
        <span className="flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1 text-xs font-medium text-background-50 dark:text-foreground-950">
          <i className="ri-fire-fill"></i>
          头条聚焦
        </span>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            aria-label="上一条"
            onClick={() => setActive((prev) => (prev - 1 + articles.length) % articles.length)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-accent-500"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            aria-label="下一条"
            onClick={() => setActive((prev) => (prev + 1) % articles.length)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-accent-500"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full p-4 md:p-6">
        <Link to={`/detail/${current.id}`} className="block max-w-2xl">
          <span className="mb-2 inline-block rounded-md bg-background-50/20 px-2 py-0.5 text-xs text-background-50 backdrop-blur">
            {current.categoryName}
          </span>
          <h2 className="font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
            {current.title}
          </h2>
          <p className="mt-2 line-clamp-2 hidden text-sm leading-relaxed text-background-200 sm:block">
            {current.summary}
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs text-background-300">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-400"></i>
              {formatNumber(current.hot)} 热度
            </span>
            <span>{timeAgo(current.publishTime)}</span>
          </div>
        </Link>
        <div className="mt-4 flex items-center gap-2">
          {articles.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`第 ${index + 1} 条`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </div>
  );
}